// @ts-nocheck
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  createContext,
  useContext,
} from "react";
import {
  Box,
  Grid,
  Container,
  Slide,
  Tooltip,
  LinearProgress,
  Skeleton,
} from "@mui/material";
import "reactjs-popup/dist/index.css";
import GridOnIcon from "@mui/icons-material/GridOn";

import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import useAppContextProvider from "../AppContext/useAppContextProvider.js";
import {
  GridDetailPanelToggleCell,
  GridNoRowsOverlay,
} from "@mui/x-data-grid-pro";
import { StyledCheckbox } from "../components/StyledComponents/Inputs/Inputs.js";
import StyledLinearProgress from "../components/StyledComponents/Progress/Progress.js";
import ArrowRight from "@mui/icons-material/ArrowRight";
import { useHistory, useLocation } from "react-router-dom";
import {
  BodyLarge,
  BodyMedium,
  BodySmall,
  H7TitleLarge,
  LabelLarge,
  LabelMedium,
} from "../components/StyledComponents/Typography/Typography.tsx";
import { Popover, Stack } from "@mui/material";
import styled from "@emotion/styled";
import PageWrapper from "../components/PageWrapper/PageWrapper.js";
import { useAuth0 } from "@auth0/auth0-react";
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";

import { CaseManagementOperationsContainer } from "./Components/CaseManagementOperationsContainer.js";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { CaseManagementHeader } from "./Components/CaseManagementHeader.js";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import useMinerPageContext from "../minerstatusV2/context/useMinerPageContext.js";
import DataGrid from "../components/DataGrid/DataGrid.tsx";

import {
  returnDefaultColumnConfigs,
  returnDefaultColumnConfigsForTable,
} from "./Utils/CaseManagementUtils.js";
import { CaseManagementTableGroup } from "../constants.js";
import { isEmpty } from "lodash";
import {
  SecondaryButton,
  StyledLabel,
} from "../components/StyledComponents/Buttons/AuradineButtons.js";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import useSiteAndGroupTreeContext from "../minerstatusV2/context/useSiteAndGroupTreeContext.js";
import AuradineErrorState from "../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import { format, parseISO, isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import MinerJobDetailsPage from "../job/Pages/MinerJobDetails.js";
import JobCompletionStepper from "../minerstatusV2/Components/JobCompletionStepper/JobCompletionStepper.js";
import {
  PostCaseList,
  GetIssueSummaryBreakdown,
  GetCaseStatusBreakdown,
  GetUserTechsupport,
} from "../api/api.js";
import { useGetCaseCommentHistory } from "./queries/useCaseCommentHistory.ts";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { IconButton } from "@mui/material";
import { useTheme } from "../ThemeContext/ThemeContext.js";
import { caseStatusOptions } from "./Components/CaseManagementFilters.js";

// Simple Error Boundary Component
class SimpleErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a ResizeObserver error
    if (
      error.message &&
      (error.message.includes("ResizeObserver loop completed with undelivered notifications") ||
       error.message.includes("ResizeObserver loop limit exceeded"))
    ) {
      // Silently ignore ResizeObserver errors
      return { hasError: false };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Check if it's a ResizeObserver error
    if (
      error.message &&
      (error.message.includes("ResizeObserver loop completed with undelivered notifications") ||
       error.message.includes("ResizeObserver loop limit exceeded"))
    ) {
      // Silently ignore ResizeObserver errors
      return;
    }
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong.</div>;
    }

    return this.props.children;
  }
}

// DataGrid Wrapper that completely disables ResizeObserver
const DataGridWrapper = (props) => {
  const { colors, config, height } = props;

  useEffect(() => {
    // Store original ResizeObserver
    const originalResizeObserver = window.ResizeObserver;

    // Completely disable ResizeObserver
    window.ResizeObserver = function() {
      return {
        observe: () => {},
        unobserve: () => {},
        disconnect: () => {}
      };
    };

    // Restore after component unmounts
    return () => {
      window.ResizeObserver = originalResizeObserver;
    };
  }, []);

  return (
    <DataGrid
      colors={colors}
      config={config}
      height={height}
    />
  );
};

// Removed global overrides - they were breaking icon rendering across the entire app
// ResizeObserver error suppression is handled within the CaseManagementPage component's useEffect hook

export const RefetchContext = createContext({
  minerPageRefetch: () => {},
});
export const useRefetch = () => useContext(RefetchContext);

// New function to handle case status processing for pie chart
const processCaseStatusForPieChart = (postCaseListResponse) => {
  console.log('Processing PostCaseList response for pie chart:', postCaseListResponse);

  if (!postCaseListResponse || postCaseListResponse.status !== "ok") {
    console.log('Invalid PostCaseList response');
    return {
      status: "ok",
      caseStatusStats: [],
      totalCases: 0
    };
  }

  // Extract caseStatusStats from the response
  const caseStatusStats = postCaseListResponse.caseStatusStats || [];
  const totalCases = postCaseListResponse.count || 0;

  console.log('Extracted caseStatusStats:', caseStatusStats);
  console.log('Total cases:', totalCases);

  return {
    status: "ok",
    caseStatusStats: caseStatusStats,
    totalCases: totalCases
  };
};


// Helper function to get status colors for pie chart
const getStatusColor = (status, colors) => {
  const statusColors = {
    New: colors?.legacy?.blue?.info || "#2962FF", // Info blue for new cases
    Active: colors?.primary?.main || "#2D55C9", // Primary blue for active cases
    "Need More Information": colors?.warning?.main || "#ff9d00", // Orange for needing info
    "Information Provided": colors?.legacy?.blue?.accent || "#3366ff", // Accent blue for info provided
    Escalated: colors?.error?.dark || "#C62828", // Dark red for escalated cases
    "Customer Action Required": colors?.warning?.dark || "#F57C00", // Dark orange for customer action
    "Pending Release": colors?.legacy?.blue?.light || "#00c3ff", // Light blue for pending release
    RMA: colors?.legacy?.misc?.purple || "#7d5fc9", // Purple for RMA
    "RMA In Progress": "#9C27B0", // Different purple shade for RMA in progress
    "Awaiting Customer Verification": colors?.legacy?.orange?.light || "#ffa500", // Light orange for awaiting verification
    "On Hold": colors?.secondary?.main || "#747685", // Gray for on hold
    Closed: colors?.success?.main || "#219150", // Green for closed cases
  };
  return statusColors[status] || colors?.primary?.main || "#2D55C9";
};

// Helper function to get consistent colors for issue types
const getIssueColor = (issue, colors) => {
  const issueColors = {
    "Administration/Management": colors?.legacy?.misc?.brown || "#423636", // Brown for admin/management
    "API": colors?.legacy?.misc?.purple || "#7d5fc9", // Purple for API/technical
    "Power Supply": colors?.warning?.main || "#ff9d00", // Orange for power issues
    "Fans Chassis": colors?.primary?.light || "#C7D1FF", // Light blue for cooling/fans
    "PSU Fans": colors?.legacy?.blue?.info || "#2962FF", // Info blue for PSU fans
    "Control Board": colors?.primary?.main || "#2D55C9", // Primary blue for core component
    "Hash Board": colors?.primary?.dark || "#1E3A8A", // Darker blue for hash boards
    "Configuration": colors?.legacy?.blue?.accent || "#3366ff", // Accent blue for configuration
    "Network/Connectivity": colors?.error?.main || "#d32f2f", // Red for network issues
    "Performance": colors?.warning?.dark || "#F57C00", // Dark orange for performance
    "Tuning": colors?.success?.main || "#219150", // Green for optimization/tuning
    "Miner UI": colors?.secondary?.main || "#747685", // Gray for UI
    "FluxVision": colors?.error?.dark || "#C62828", // Dark red for FluxVision issues
    "ZTP": colors?.success?.dark || "#1B5E20", // Dark green for zero-touch provisioning
    "Chassis/System": colors?.legacy?.blue?.material || "#2196f3", // Material blue for system-level issues
    "General Query": colors?.text?.muted || "#55575e", // Gray for general queries
  };
  return issueColors[issue] || colors?.secondary?.light || "#E0E0E0";
};

// LinearProgressWithLabel component
const LinearProgressWithLabel = ({
  value,
  label,
  count,
  total,
  color,
  onClick,
  colors,
}) => {
  const progressColor = color || colors?.primary?.main;

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <LabelMedium
          style={{
            color: colors?.text?.secondary,
            cursor: onClick ? "pointer" : "default",
            "&:hover": onClick ? { textDecoration: "underline" } : {},
          }}
          onClick={onClick}
        >
          {label}
        </LabelMedium>
        <Box display="flex" gap={1} alignItems="center">
          <BodySmall style={{ color: colors?.text?.muted }}>
            {count}/{total}
          </BodySmall>
          <BodySmall style={{ color: colors?.text?.muted }}>
            {value.toFixed(1)}%
          </BodySmall>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: colors?.border?.muted,
          "& .MuiLinearProgress-bar": {
            backgroundColor: progressColor,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
};

export const HeaderContainer = React.memo((props) => {
  const { activeView, setActiveView } = props;
  const { colors, currentTheme } = useTheme();

  return (
    <Box
      className={"header-container"}
      display={"flex"}
      justifyContent="flex-start"
      flexDirection="column"
      gap={4}
      marginBottom={"1.5rem"}
    >
      <Box
        className={"title-box"}
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={3.5}
      >
        <Box>
          <H7TitleLarge>Case Management</H7TitleLarge>
          <BodyLarge>
            Manage and track support cases for your mining devices. Create,
            update, and monitor case status and resolution progress.
          </BodyLarge>
        </Box>

        {/* View Toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <SecondaryButton
            colors={colors}
            onClick={() => setActiveView("analytics")}
            sx={{
              backgroundColor:
                activeView === "analytics"
                  ? colors?.primary?.light
                  : "transparent",
              color:
                activeView === "analytics"
                  ? currentTheme === "dark"
                    ? "#FFFFFF !important"
                    : colors?.primary?.main
                  : colors?.text?.muted,
              fontWeight: activeView === "analytics" ? 600 : 400,
              borderRadius: "1.5rem",
              padding: "0.5rem 1.25rem",
              border:
                activeView === "analytics"
                  ? "none"
                  : `1px solid ${colors?.border?.muted || colors?.border?.default}`,
              "&:hover": {
                backgroundColor:
                  activeView === "analytics"
                    ? colors?.primary?.light
                    : colors?.hover?.muted || "rgba(0, 0, 0, 0.04)",
                color:
                  activeView === "analytics"
                    ? currentTheme === "dark"
                      ? "#FFFFFF !important"
                      : colors?.primary?.main
                    : colors?.text?.primary,
                border:
                  activeView === "analytics"
                    ? "none"
                    : `1px solid ${colors?.border?.default}`,
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Analytics
          </SecondaryButton>
          <BodyMedium
            sx={{
              color: colors?.text?.muted,
              mx: 0.5,
              opacity: 0.5,
            }}
          >
            |
          </BodyMedium>
          <SecondaryButton
            colors={colors}
            onClick={() => setActiveView("allCases")}
            sx={{
              backgroundColor:
                activeView === "allCases"
                  ? colors?.primary?.light
                  : "transparent",
              color:
                activeView === "allCases"
                  ? currentTheme === "dark"
                    ? "#FFFFFF !important"
                    : colors?.primary?.main
                  : colors?.text?.muted,
              fontWeight: activeView === "allCases" ? 600 : 400,
              borderRadius: "1.5rem",
              padding: "0.5rem 1.25rem",
              border:
                activeView === "allCases"
                  ? "none"
                  : `1px solid ${colors?.border?.muted || colors?.border?.default}`,
              "&:hover": {
                backgroundColor:
                  activeView === "allCases"
                    ? colors?.primary?.light
                    : colors?.hover?.muted || "rgba(0, 0, 0, 0.04)",
                color:
                  activeView === "allCases"
                    ? currentTheme === "dark"
                      ? "#FFFFFF !important"
                      : colors?.primary?.main
                    : colors?.text?.primary,
                border:
                  activeView === "allCases"
                    ? "none"
                    : `1px solid ${colors?.border?.default}`,
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Manage Cases
          </SecondaryButton>
        </Box>
      </Box>
    </Box>
  );
});

export default function CaseManagementPage() {
  const { colors } = useTheme();
  // Theme-aware colors for metric cards
  const metricColors = {
    activeCases: colors?.success?.main, // Green
    totalCases: colors?.primary?.main, // Blue
    rmaCases: colors?.legacy?.misc?.purple || "#7d5fc9", // Purple (matches RMA status color)
    last7Days: colors?.primary?.light, // Cyan
    last30Days: colors?.success?.light, // Light Green
  };

  const { user, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const location = useLocation();
  const history = useHistory();
  const [hasAccess, setHasAccess] = useState(null); // null = checking, true = has access, false = no access
  const [selectionModel, setSelectionModel] = useState([]);
  const [selectedCases, setSelectedCases] = useState([]);
  const [authToken, setAuthToken] = useState("");
  const [caseFilters, setCaseFilters] = useState({});
  const [sortModel, setSortModel] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [caseCount, setCaseCount] = useState(0);

  const [tableKey, setTableKey] = useState(0);
  const [openMinerTransferDialog, setOpenMinerTransferDialog] = useState(false);
  const [limit, setLimit] = useState(25);
  const [gridHeight, setGridHeight] = useState("calc(100vh - 23rem)");
  const [rightSidePanelOpen, setRightSidePanelOpen] = useState(false);
  const pieChartRef = useRef(null);
  const panelToggleTimeoutRef = useRef(null);
  // Initialize activeView from sessionStorage if available, otherwise default to "analytics"
  const [activeView, setActiveView] = useState(() => {
    const savedView = sessionStorage.getItem("caseManagementActiveView");
    return savedView === "analytics" || savedView === "allCases" ? savedView : "analytics";
  }); // 'analytics' or 'allCases'
  const [processedCaseStatusData, setProcessedCaseStatusData] = useState({
    status: "ok",
    caseStatusStats: [],
    totalCases: 0
  });

  // Use state for calculated values to prevent ResizeObserver loops
  const [last7DaysCount, setLast7DaysCount] = useState(0);
  const [last30DaysCount, setLast30DaysCount] = useState(0);
  const [dateRanges, setDateRanges] = useState(null);

  // Calculate date ranges once on mount
  useEffect(() => {
    const now = new Date();

    // Last 7 days
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    // Last 30 days
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);

    setDateRanges({
      now,
      sevenDaysAgo,
      thirtyDaysAgo
    });
  }, []);

  // Case management is now enabled for everyone
  useEffect(() => {
    if (!isAuthenticated) {
      setHasAccess(false);
    } else {
      setHasAccess(true);
    }
  }, [isAuthenticated]);

  // Get comment history for selected case
  const selectedCaseId = selectedCases.length === 1 ? selectedCases[0] : null;
  const { data: commentHistoryData, isLoading: commentHistoryLoading, error: commentHistoryError } =
    useGetCaseCommentHistory(
    selectedCaseId,
      rightSidePanelOpen && selectedCaseId !== null && hasAccess !== false
    );

  // Debug logging (commented out - uncomment for debugging)
  // console.log('Comment History Debug:', {
  //   selectedCaseId,
  //   rightSidePanelOpen,
  //   commentHistoryData,
  //   commentHistoryLoading,
  //   commentHistoryError
  // });

  // Persist activeView to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("caseManagementActiveView", activeView);
  }, [activeView]);

  // Close right panel and clear selection when switching to analytics view
  useEffect(() => {
    if (activeView === "analytics") {
      setRightSidePanelOpen(false);
      setSelectedCases([]);
      setSelectionModel([]);
    }
  }, [activeView]);

  // REMOVED: This useEffect was conflicting with onRowSelectionModelChange handler
  // Panel state is now controlled solely by onRowSelectionModelChange to prevent race conditions
  // useEffect(() => {
  //   if (selectedCases.length === 0) {
  //     setRightSidePanelOpen(false);
  //   }
  // }, [selectedCases.length]);

  // Handle window resize (e.g., when DevTools opens/closes) to force DataGrid recalculation
  useEffect(() => {
    // Debounce resize events to avoid too many recalculations
    let resizeTimeout;
    const debouncedHandleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Force DataGrid to recalculate width when viewport changes
        // This handles cases like DevTools opening/closing
        if (activeView === "allCases") {
          // Use a small delay to ensure the resize is complete
          setTableKey((prev) => prev + 1);
        }
        // Trigger pie chart reflow on window resize
        if (pieChartRef.current && pieChartRef.current.chart) {
          pieChartRef.current.chart.reflow();
        }
      }, 200);
    };

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      clearTimeout(resizeTimeout);
    };
  }, [activeView]);



  // Add ResizeObserver error handling
  React.useEffect(() => {
    const handleResizeObserverError = (e) => {
      if (
        e.message &&
        e.message.includes(
          "ResizeObserver loop completed with undelivered notifications"
        )
      ) {
        // Ignore this specific error as it's harmless
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (e) => {
      if (
        e.reason &&
        e.reason.message &&
        e.reason.message.includes(
          "ResizeObserver loop completed with undelivered notifications"
        )
      ) {
        // Ignore this specific error as it's harmless
        e.preventDefault();
        return false;
      }
    };

    // Override console.error to suppress ResizeObserver errors
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const message = args.join(" ");
      if (
        message.includes(
          "ResizeObserver loop completed with undelivered notifications"
        )
      ) {
        // Suppress this specific error
        return;
      }
      originalConsoleError.apply(console, args);
    };

    window.addEventListener("error", handleResizeObserverError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleResizeObserverError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
      // Restore original console.error
      console.error = originalConsoleError;
    };
  }, []);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (panelToggleTimeoutRef.current) {
        clearTimeout(panelToggleTimeoutRef.current);
      }
    };
  }, []);

  // Handle right side panel toggle with cleanup to prevent stale updates
  const handleRightSidePanelToggle = (open) => {
    // Clear any pending timeout to prevent stale updates
    if (panelToggleTimeoutRef.current) {
      clearTimeout(panelToggleTimeoutRef.current);
      panelToggleTimeoutRef.current = null;
    }

    if (open) {
      // Reduced delay and proper cleanup
      panelToggleTimeoutRef.current = setTimeout(() => {
        setRightSidePanelOpen(true);
        panelToggleTimeoutRef.current = null;
      }, 100);
    } else {
      setRightSidePanelOpen(false);
    }
  };

  // Debounced resize handler to prevent ResizeObserver loops
  const debouncedResizeHandler = useRef(null);
  useEffect(() => {
    const handleResize = () => {
      if (debouncedResizeHandler.current) {
        clearTimeout(debouncedResizeHandler.current);
      }
      debouncedResizeHandler.current = setTimeout(() => {
        // Trigger any necessary layout updates here
        if (tableContainerRef.current) {
          // Force a gentle resize of the DataGrid
          const event = new Event("resize");
          window.dispatchEvent(event);
        }
      }, 200);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (debouncedResizeHandler.current) {
        clearTimeout(debouncedResizeHandler.current);
      }
    };
  }, []);


  const tableContainerRef = useRef(null);

  // Fetch authentication token
  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn("User is not authenticated. Fetching token...");
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, getAccessTokenSilently]);

  // URL parameter handling - same pattern as MinerPage
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasParams = Array.from(params.keys()).length > 0;

    let filters = {};

    const paramKeys = [
      "caseNo",
      "caseNo1",
      "caseNoListName",
      "issue",
      "issue1",
      "issueListName",
      "serialNo",
      "serialNo1",
      "caseStatus",
      "createdBy",
      "createdBy1",
      "updatedBy",
      "updatedBy1",
      "createdAtFrom",
      "createdAtTo",
      "updatedAtFrom",
      "updatedAtTo",
    ];

    if (hasParams) {
      paramKeys.forEach((key) => {
        if (
          [
            "caseStatus",
            "issue",
            "caseNo",
            "serialNo",
            "createdBy",
            "updatedBy",
          ].includes(key)
        ) {
          const values = params.getAll(key);
          if (values.length > 0) {
            filters[key] = values.map(String);
          }
        } else {
          const value = params.get(key);
          if (value) {
            filters[key] = value;
          }
        }
      });

      sessionStorage.setItem("caseFilters", JSON.stringify(filters));
      setCaseFilters(filters);
    } else {
      const newParams = new URLSearchParams();

      const savedFilters = sessionStorage.getItem("caseFilters");
      if (savedFilters) {
        const storedFilters = JSON.parse(savedFilters);
        const fieldsThatShouldBeArrays = [
          "caseStatus",
          "issue",
          "caseNo",
          "serialNo",
          "createdBy",
          "updatedBy",
        ];

        Object.keys(storedFilters).forEach((key) => {
          if (fieldsThatShouldBeArrays.includes(key)) {
            if (!Array.isArray(storedFilters[key])) {
              storedFilters[key] = [storedFilters[key]];
            }
          }
        });

        setCaseFilters(storedFilters);

        for (const [key, val] of Object.entries(storedFilters)) {
          if (Array.isArray(val)) {
            val.forEach((v) => newParams.append(key, v));
          } else {
            newParams.set(key, val);
          }
        }
      }

      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${newParams.toString()}`
      );
    }
  }, [location.search]);

  // Listen for filter changes
  useEffect(() => {
    const handleFiltersChanged = (e) => {
      setCaseFilters(e.detail.filters);
    };

    window.addEventListener("caseFiltersChanged", handleFiltersChanged);
    return () =>
      window.removeEventListener("caseFiltersChanged", handleFiltersChanged);
  }, []);

  // API integration for case list
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isFetched,
    isLoading: isMinerDataLoading,
    refetch: minerPageRefetch,
  } = useInfiniteQuery({
    queryKey: [
      "caseList",
      JSON.stringify(caseFilters),
      JSON.stringify(sortModel),
    ],
    queryFn: ({ pageParam = { skip: 0, limit } }) =>
      PostCaseList({
        authToken,
        body: {
          filters: caseFilters,
          sort:
            sortModel.length > 0
              ? { [sortModel[0].field]: sortModel[0].sort }
              : {},
          pagination: {
            limit: pageParam.limit,
            skip: pageParam.skip,
          },
        },
      }),
    enabled: !!authToken,
    staleTime: 0,
    keepPreviousData: false,
    gcTime: 0,
    initialPageParam: { skip: 0, limit },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flatMap((page) =>
        page.status === "ok" && page.msg && Array.isArray(page.msg)
          ? page.msg
          : []
      ).length;
      // Use totalCount from API response instead of msg.length
      const totalCount =
        lastPage?.status === "ok" ? lastPage?.totalCount || 0 : 0;
      return totalFetched >= totalCount
        ? undefined
        : { skip: totalFetched, limit };
    },
  });

  // Separate query for issue summary breakdown (no filters applied)
  const {
    data: issueSummaryData,
    isLoading: isIssueSummaryLoading,
    refetch: refetchIssueSummary,
  } = useQuery({
    queryKey: ["issueSummaryBreakdown"],
    queryFn: () => GetIssueSummaryBreakdown({ authToken }),
    enabled: !!authToken,
  });

  // Separate query for case status breakdown
  // For Analytics view: always use empty filters to show all data
  // For Manage Cases view: use caseFilters (but this query is primarily for Analytics)
  const {
    data: caseStatusData,
    isLoading: isCaseStatusLoading,
    refetch: refetchCaseStatus,
  } = useQuery({
    queryKey: ["caseStatusBreakdown"],
    queryFn: () => GetCaseStatusBreakdown({ authToken, filters: {} }),
    enabled: !!authToken,
    onSuccess: (data) => {
      console.log('Case Status Data received:', data);
      console.log('Case Status Stats:', data?.caseStatusStats);
    },
    onError: (error) => {
      console.error('Case Status Data error:', error);
    },
  });

  // Use processed case status data if available, otherwise use original caseStatusData
  const finalCaseStatusData = processedCaseStatusData.caseStatusStats.length > 0
    ? processedCaseStatusData
    : caseStatusData;

  const resetTableSelection = () => {
    setSelectionModel([]);
    setSelectedCases([]);
  };

  // Function to handle scroll end event and fetch next page
  const handleScrollEnd = () => {
    if (!isFetchingNextPage && !isMinerDataLoading) {
      fetchNextPage();
    }
  };

  const downloadCSV = () => {
    // Implement CSV download functionality
    console.log("Download CSV");
  };

  // Get default column configurations
  const columns = returnDefaultColumnConfigsForTable(colors);

  // Get table group from user preferences or use default
  const tableGroup =
    user?.userPreferences?.tableGroup || CaseManagementTableGroup;

  const tableDetailColOrder =
    tableGroup.length > 0 ? tableGroup?.map((item) => item?.id) : [];

  // Create a new array of columns sorted by the order in tableDetailColOrder
  const sortedColumns = tableDetailColOrder
    .map((id) => columns.find((column) => column?.field === id))
    .filter((column) => column !== undefined)
    .map((column) => ({ ...column, hide: false })); // Remove undefined columns here

  // Add the rest of the columns to the new array and set their hide property to true
  const restOfColumns = columns
    .filter((column) => !tableDetailColOrder.includes(column?.field))
    .map((column) => ({ ...column, hide: true })); // Ensure all non-included columns are hidden

  const finalColumns = [...sortedColumns, ...restOfColumns];

  // Process API data for the table
  const filterSignatureRef = useRef("");
  const getFilterSignature = () =>
    JSON.stringify({
      caseFilters,
    });

  const currentSignature = getFilterSignature();

  // Update filter signature ref synchronously to prevent race conditions
  // When filters change, reset the ref to force data clearing
  if (filterSignatureRef.current !== currentSignature) {
    filterSignatureRef.current = currentSignature;
  }

  let tableData = [];
  // Only show data if:
  // 1. Data exists and has pages
  // 2. The current filter signature matches the ref (ensures we're showing data for current filters)
  // 3. The query has been fetched (isFetched)
  // 4. We're not in initial loading state (isMinerDataLoading) - but allow showing data while fetching next page
  // Note: We allow showing data during isFetchingNextPage to maintain smooth scrolling experience
  if (
    data &&
    data.pages &&
    data.pages.length > 0 &&
    currentSignature === filterSignatureRef.current &&
    isFetched &&
    !isMinerDataLoading
  ) {
    tableData = data.pages
      .filter((page) => page.status === "ok" && page.msg && Array.isArray(page.msg))
      .flatMap((page, pageIndex) => {
        const cases = page.msg;
        return cases.map((item, index) => {
          // Use the actual caseId as the row ID
          return {
            ...item,
            id: item.caseId, // Use the actual caseId instead of synthetic ID
            // rowNo: pageIndex * limit + index + 1,
          };
        });
      });
  } else {
    // Clear tableData when filters change, data is not ready, or initial query is loading
    tableData = [];
  }


  // Calculate counts asynchronously to prevent ResizeObserver loops
  useEffect(() => {
    if (!tableData || !dateRanges) return;

    // Temporarily disable ResizeObserver during calculations
    const originalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = function() { return { observe: () => {}, unobserve: () => {}, disconnect: () => {} }; };

    // Use requestAnimationFrame to defer the calculation
    const calculateCounts = () => {
      const sevenDaysCount = tableData.filter((caseItem) => {
        if (!caseItem.updatedAt) return false;
        const caseDate = new Date(caseItem.updatedAt);
        return caseDate >= dateRanges.sevenDaysAgo;
      }).length;

      const thirtyDaysCount = tableData.filter((caseItem) => {
        if (!caseItem.updatedAt) return false;
        const caseDate = new Date(caseItem.updatedAt);
        return caseDate >= dateRanges.thirtyDaysAgo;
      }).length;

      setLast7DaysCount(sevenDaysCount);
      setLast30DaysCount(thirtyDaysCount);

      // Restore ResizeObserver after calculations
      setTimeout(() => {
        window.ResizeObserver = originalResizeObserver;
      }, 100);
    };

    // Defer to next frame to avoid ResizeObserver loops
    requestAnimationFrame(calculateCounts);
  }, [tableData, dateRanges]);

  // Note: filterSignatureRef is now updated synchronously in the tableData computation
  // This useEffect is kept for any other potential side effects, but the ref update happens above

  // Update total count when data changes
  useEffect(() => {
    if (data && data.pages && data.pages.length > 0) {
      const lastPage = data.pages[data.pages.length - 1];
      if (lastPage && lastPage.status === "ok") {
        // Use totalCount from API response instead of msg.length
        setTotalCount(lastPage.totalCount || 0);
      } else {
        setTotalCount(0);
      }
    } else {
      setTotalCount(0);
    }
  }, [data]);

  // Show loading or redirect if no access (after all hooks are called)
  if (hasAccess === null) {
    return (
      <PageWrapper>
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
            <Skeleton variant="rectangular" width="100%" height={400} animation="wave" />
          </Box>
        </Container>
      </PageWrapper>
    );
  }

  if (hasAccess === false) {
    return null; // Will redirect, so return nothing
  }

  const dataGridProps = {
    rows: tableData || [],
    columns: finalColumns,
    sortingMode: "server",
    page: 0,
    loading: isFetchingNextPage || isMinerDataLoading,
    paginationMode: "client",
    getRowId: (row) => row.id,
    pagination: true,
    pageSize: tableData?.length || 0,
    paginationModel: { page: 0, pageSize: totalCount || 0 },
    disableColumnMenu: true,
    disableColumnResize: true,
    checkboxSelection: true,
    disableSelectionOnClick: true,
    pinnedColumns: {
      left: ["__check__"],
    },
    disableCellSelection: true,
    overlayNoRowsTemplate: "No cases found",
    slots: {
      baseCheckbox: StyledCheckbox,
      loadingOverlay: () => <StyledLinearProgress colors={colors} />,
      noRowsOverlay: () => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <BodyMedium>No cases found</BodyMedium>
        </Box>
      ),
    },
    hideFooterPagination: true,
    hideFooter: true,
    selectionModel: Array.isArray(selectionModel) ? selectionModel : [],
    onRowSelectionModelChange: (newSelection) => {
      // Validate selection array
      const validSelection = Array.isArray(newSelection) ? newSelection : [];

      // Update both state variables together
      setSelectionModel(validSelection);
      setSelectedCases(validSelection);

      // Single source of truth for panel state - handle all cases
      if (validSelection.length === 0) {
        setRightSidePanelOpen(false);
      } else if (validSelection.length === 1) {
        // Use requestAnimationFrame for better timing than setTimeout
        requestAnimationFrame(() => {
          setRightSidePanelOpen(true);
        });
      } else {
        // Multiple selections - close panel
        setRightSidePanelOpen(false);
      }
    },
    // REMOVED onRowClick to match Miner Inventory pattern
    // With disableSelectionOnClick: true, row clicks don't change selection anyway
    // Panel opening is handled by onRowSelectionModelChange based on selection count
    // If we need row-click-to-open-panel functionality, we can add it back later in a safer way
    sortModel: sortModel,
    onSortModelChange: (newSortModel) => {
      setSortModel(newSortModel);
    },
    onRowsScrollEnd: handleScrollEnd,
    getRowHeight: () => "auto",
    // Additional properties to reduce ResizeObserver issues
    autoHeight: false,
    density: "compact",
    disableVirtualization: false,
  };

  return (
    <RefetchContext.Provider value={{ minerPageRefetch }}>
      <PageWrapper>
        <HeaderContainer
          activeView={activeView}
          setActiveView={setActiveView}
        />

        {/* Conditional rendering based on activeView */}
        {activeView === "analytics" ? (
          // Analytics View
          <Box
            sx={{
              backgroundColor: colors?.background?.paper,
              borderRadius: "1rem",
              padding: "0.75rem 0 0.75rem 0",
              marginBottom: "1rem",
              transition: "width 0.3s ease-in-out",
            }}
          >
          <Box
            sx={{
              padding: "0 0.75rem 0 0.75rem",
            }}
          >
                    <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
                <H7TitleLarge>Analytics</H7TitleLarge>
                {(() => {
                  // Check if there are active filters
                  const hasActiveFilters = Object.entries(caseFilters).some(
                    ([key, value]) =>
                      value !== undefined &&
                      value !== null &&
                      value !== "" &&
                      (!Array.isArray(value) || value.length > 0)
                  );

                  return hasActiveFilters ? (
                    <SecondaryButton
                      colors={colors}
                      onClick={() => {
                        const newFilters = {};
                        setCaseFilters(newFilters);

                        // Clear processed case status data to show all cases
                        setProcessedCaseStatusData({
                          status: "ok",
                          caseStatusStats: [],
                          totalCases: 0
                        });

                        sessionStorage.setItem(
                          "caseFilters",
                          JSON.stringify(newFilters)
                        );

                        // Update URL parameters - clear all
                        const url = new URL(window.location.href);
                        url.search = ""; // Clear all search parameters
                        window.history.replaceState({}, "", url);

                        window.dispatchEvent(
                          new CustomEvent("caseFiltersChanged", {
                            detail: { filters: newFilters },
                          })
                        );
                      }}
                      sx={{
                        minWidth: "120px",
                        height: "36px",
                        fontSize: "0.875rem",
                      }}
                    >
                      Clear Filters
                    </SecondaryButton>
                  ) : null;
                })()}
          </Box>
                                    <Grid container spacing={3}>
              {/* First Row: Metric Cards */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {/* Active Cases */}
                  <Grid item xs={6} sm={2.4}>
                      <Box
                        sx={{
                      height: "80px",
                      borderRadius: "1rem",
                          border: `1px solid ${colors?.border?.card}`,
                          backgroundColor: colors?.background?.card,
                      padding: "0.75rem",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                              backgroundColor: metricColors.activeCases,
                            }}
                          />
                          <BodyMedium
                            sx={{
                              color: colors?.text?.secondary,
                              fontSize: "0.75rem",
                            }}
                          >
                          Active Cases
                        </BodyMedium>
                      </Box>
                      <Box>
                          {isCaseStatusLoading ? (
                            <Skeleton
                              variant="rectangular"
                              width={60}
                              height={28}
                              animation="wave"
                              sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                            />
                          ) : (
                            <H7TitleLarge
                              sx={{
                                color: colors?.primary?.main,
                                fontSize: "1.25rem",
                              }}
                            >
                              {(() => {
                                if (finalCaseStatusData?.caseStatusStats) {
                                  return finalCaseStatusData.caseStatusStats
                                    .filter(item =>
                                      item.status !== "Closed" &&
                                      item.status !== "Resolved"
                                    )
                                    .reduce((sum, item) => sum + item.count, 0);
                                }
                                return tableData?.filter(
                                  (caseItem) =>
                                    caseItem.caseStatus !== "Closed" &&
                                    caseItem.caseStatus !== "Resolved"
                                ).length || 0;
                              })()}
                        </H7TitleLarge>
                          )}
                      </Box>
                      <Box
                        onClick={() => {
                            setActiveView("allCases");
                          // Filter out "Closed" status for Active Cases
                          const activeCaseStatuses = caseStatusOptions.filter(status => status !== "Closed");
                          const newFilters = {
                              caseStatus: activeCaseStatuses,
                          };
                          setCaseFilters(newFilters);
                            sessionStorage.setItem(
                              "caseFilters",
                              JSON.stringify(newFilters)
                            );

                            // Update URL parameters
                            const url = new URL(window.location.href);
                            url.search = ""; // Clear all search parameters
                            newFilters.caseStatus.forEach((status) => {
                              url.searchParams.append("caseStatus", status);
                            });
                            window.history.replaceState({}, "", url);

                            window.dispatchEvent(
                              new CustomEvent("caseFiltersChanged", {
                                detail: { filters: newFilters },
                              })
                            );
                        }}
                        sx={{
                          position: "absolute",
                          top: "0.5rem",
                          right: "0.5rem",
                          cursor: "pointer",
                          fontSize: "1.25rem",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                              transform: "scale(1.1)",
                            },
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                            style={{
                              fontSize: "1.1rem",
                              color: colors?.text?.muted,
                            }}
                        >
                          open_in_new
                        </span>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Total Cases */}
                  <Grid item xs={6} sm={2.4}>
                      <Box
                        sx={{
                      height: "80px",
                      borderRadius: "1rem",
                          border: `1px solid ${colors?.border?.card}`,
                          backgroundColor: colors?.background?.card,
                      padding: "0.75rem",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                              backgroundColor: metricColors.totalCases,
                            }}
                          />
                          <BodyMedium
                            sx={{
                              color: colors?.text?.secondary,
                              fontSize: "0.75rem",
                            }}
                          >
                          Total Cases
                        </BodyMedium>
                      </Box>
                      <Box>
                          {isCaseStatusLoading ? (
                            <Skeleton
                              variant="rectangular"
                              width={60}
                              height={28}
                              animation="wave"
                              sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                            />
                          ) : (
                            <H7TitleLarge
                              sx={{
                                color: colors?.primary?.main,
                                fontSize: "1.25rem",
                              }}
                            >
                              {finalCaseStatusData?.totalCases || tableData?.length || 0}
                        </H7TitleLarge>
                          )}
                      </Box>
                      <Box
                        onClick={() => {
                            setActiveView("allCases");
                          const newFilters = {};
                          setCaseFilters(newFilters);

                            // Clear processed case status data to show all cases
                            setProcessedCaseStatusData({
                              status: "ok",
                              caseStatusStats: [],
                              totalCases: 0
                            });

                            sessionStorage.setItem(
                              "caseFilters",
                              JSON.stringify(newFilters)
                            );

                            // Update URL parameters - clear all
                            const url = new URL(window.location.href);
                            url.search = ""; // Clear all search parameters
                            window.history.replaceState({}, "", url);

                            window.dispatchEvent(
                              new CustomEvent("caseFiltersChanged", {
                                detail: { filters: newFilters },
                              })
                            );
                        }}
                        sx={{
                          position: "absolute",
                          top: "0.5rem",
                          right: "0.5rem",
                          cursor: "pointer",
                          fontSize: "1.25rem",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                              transform: "scale(1.1)",
                            },
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                            style={{
                              fontSize: "1.1rem",
                              color: colors?.text?.muted,
                            }}
                        >
                          open_in_new
                        </span>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Total RMA Cases */}
                  <Grid item xs={6} sm={2.4}>
                      <Box
                        sx={{
                      height: "80px",
                      borderRadius: "1rem",
                          border: `1px solid ${colors?.border?.card}`,
                          backgroundColor: colors?.background?.card,
                      padding: "0.75rem",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                              backgroundColor: metricColors.rmaCases,
                            }}
                          />
                          <BodyMedium
                            sx={{
                              color: colors?.text?.secondary,
                              fontSize: "0.75rem",
                            }}
                          >
                          RMA Cases
                        </BodyMedium>
                      </Box>
                      <Box>
                          {isCaseStatusLoading ? (
                            <Skeleton
                              variant="rectangular"
                              width={60}
                              height={28}
                              animation="wave"
                              sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                            />
                          ) : (
                            <H7TitleLarge
                              sx={{
                                color: colors?.primary?.main,
                                fontSize: "1.25rem",
                              }}
                            >
                              {(() => {
                                if (finalCaseStatusData?.caseStatusStats) {
                                  return finalCaseStatusData.caseStatusStats
                                    .filter(item =>
                                      item.status === "RMA" || item.status === "RMA In Progress"
                                    )
                                    .reduce((sum, item) => sum + item.count, 0);
                                }
                                return tableData?.filter(
                                  (caseItem) => caseItem.caseStatus === "RMA" || caseItem.caseStatus === "RMA In Progress"
                                ).length || 0;
                              })()}
                        </H7TitleLarge>
                          )}
                      </Box>
                      <Box
                        onClick={() => {
                            setActiveView("allCases");
                          const newFilters = {
                              caseStatus: ["RMA", "RMA In Progress"],
                          };
                          setCaseFilters(newFilters);
                            sessionStorage.setItem(
                              "caseFilters",
                              JSON.stringify(newFilters)
                            );

                            // Update URL parameters
                            const url = new URL(window.location.href);
                            url.search = ""; // Clear all search parameters
                            newFilters.caseStatus.forEach((status) => {
                              url.searchParams.append("caseStatus", status);
                            });

                            window.history.replaceState({}, "", url);

                            window.dispatchEvent(
                              new CustomEvent("caseFiltersChanged", {
                                detail: { filters: newFilters },
                              })
                            );
                        }}
                        sx={{
                          position: "absolute",
                          top: "0.5rem",
                          right: "0.5rem",
                          cursor: "pointer",
                          fontSize: "1.25rem",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                              transform: "scale(1.1)",
                            },
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                            style={{
                              fontSize: "1.1rem",
                              color: colors?.text?.muted,
                            }}
                        >
                          open_in_new
                        </span>
                      </Box>
                    </Box>
                  </Grid>

                    {/* Cases Last 7 Days */}
                  <Grid item xs={6} sm={2.4}>
                      <Box
                        sx={{
                      height: "80px",
                      borderRadius: "1rem",
                          border: `1px solid ${colors?.border?.card}`,
                          backgroundColor: colors?.background?.card,
                      padding: "0.75rem",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                              backgroundColor: metricColors.last7Days,
                            }}
                          />
                          <BodyMedium
                            sx={{
                              color: colors?.text?.secondary,
                              fontSize: "0.75rem",
                            }}
                          >
                            Last 7 Days
                        </BodyMedium>
                      </Box>
                      <Box>
                          {isCaseStatusLoading || isMinerDataLoading ? (
                            <Skeleton
                              variant="rectangular"
                              width={60}
                              height={28}
                              animation="wave"
                              sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                            />
                          ) : (
                            <H7TitleLarge
                              sx={{
                                color: colors?.primary?.main,
                                fontSize: "1.25rem",
                              }}
                            >
                              {last7DaysCount}
                        </H7TitleLarge>
                          )}
                      </Box>
                                                                  <Box
                        onClick={() => {
                            setActiveView("allCases");

                          const newFilters = {
                              createdAtFrom: dateRanges?.sevenDaysAgo?.toISOString() || new Date().toISOString(),
                              createdAtTo: dateRanges?.now?.toISOString() || new Date().toISOString(),
                          };
                          setCaseFilters(newFilters);
                            sessionStorage.setItem(
                              "caseFilters",
                              JSON.stringify(newFilters)
                            );

                            // Update URL parameters
                            const url = new URL(window.location.href);
                            url.searchParams.delete("createdAtFrom");
                            url.searchParams.delete("createdAtTo");
                            url.searchParams.set(
                              "createdAtFrom",
                              dateRanges?.sevenDaysAgo?.toISOString() || new Date().toISOString()
                            );
                            url.searchParams.set(
                              "createdAtTo",
                              dateRanges?.now?.toISOString() || new Date().toISOString()
                            );
                            window.history.replaceState({}, "", url);

                            window.dispatchEvent(
                              new CustomEvent("caseFiltersChanged", {
                                detail: { filters: newFilters },
                              })
                            );
                        }}
                        sx={{
                          position: "absolute",
                          top: "0.5rem",
                          right: "0.5rem",
                          cursor: "pointer",
                          fontSize: "1.25rem",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                              transform: "scale(1.1)",
                            },
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                            style={{
                              fontSize: "1.1rem",
                              color: colors?.text?.muted,
                            }}
                        >
                          open_in_new
                        </span>
                      </Box>
                    </Box>
                  </Grid>

                    {/* Cases Last 30 Days */}
                  <Grid item xs={6} sm={2.4}>
                      <Box
                        sx={{
                      height: "80px",
                      borderRadius: "1rem",
                          border: `1px solid ${colors?.border?.card}`,
                          backgroundColor: colors?.background?.card,
                      padding: "0.75rem",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                              backgroundColor: metricColors.last30Days,
                            }}
                          />
                          <BodyMedium
                            sx={{
                              color: colors?.text?.secondary,
                              fontSize: "0.75rem",
                            }}
                          >
                            Last 30 Days
                        </BodyMedium>
                      </Box>
                      <Box>
                          {isCaseStatusLoading || isMinerDataLoading ? (
                            <Skeleton
                              variant="rectangular"
                              width={60}
                              height={28}
                              animation="wave"
                              sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                            />
                          ) : (
                            <H7TitleLarge
                              sx={{
                                color: colors?.primary?.main,
                                fontSize: "1.25rem",
                              }}
                            >
                              {last30DaysCount}
                        </H7TitleLarge>
                          )}
                      </Box>
                      <Box
                        onClick={() => {
                            setActiveView("allCases");

                          const newFilters = {
                              createdAtFrom: dateRanges?.thirtyDaysAgo?.toISOString() || new Date().toISOString(),
                              createdAtTo: dateRanges?.now?.toISOString() || new Date().toISOString(),
                          };
                          setCaseFilters(newFilters);
                            sessionStorage.setItem(
                              "caseFilters",
                              JSON.stringify(newFilters)
                            );

                            // Update URL parameters
                            const url = new URL(window.location.href);
                            url.searchParams.delete("createdAtFrom");
                            url.searchParams.delete("createdAtTo");
                            url.searchParams.set(
                              "createdAtFrom",
                              dateRanges?.thirtyDaysAgo?.toISOString() || new Date().toISOString()
                            );
                            url.searchParams.set(
                              "createdAtTo",
                              dateRanges?.now?.toISOString() || new Date().toISOString()
                            );
                            window.history.replaceState({}, "", url);

                            window.dispatchEvent(
                              new CustomEvent("caseFiltersChanged", {
                                detail: { filters: newFilters },
                              })
                            );
                        }}
                        sx={{
                          position: "absolute",
                          top: "0.5rem",
                          right: "0.5rem",
                          cursor: "pointer",
                          fontSize: "1.25rem",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                              transform: "scale(1.1)",
                            },
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                            style={{
                              fontSize: "1.1rem",
                              color: colors?.text?.muted,
                            }}
                        >
                          open_in_new
                        </span>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Second Row: Case Status Pie Chart */}
              <Grid item xs={12}>
                  <Box
                    sx={{
                      minHeight: "400px",
                      height: "auto",
                      width: "100%",
                      borderRadius: "1rem",
                      border: `1px solid ${colors?.border?.card}`,
                      backgroundColor: colors?.background?.card,
                      padding: "1.5rem",
                      overflow: "visible",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                    }}
                  >
                    {finalCaseStatusData?.status === "ok" &&
                    finalCaseStatusData.caseStatusStats &&
                    finalCaseStatusData.caseStatusStats.length > 0 ? (
                    <Box
                      sx={{
                        flex: 1,
                        width: "100%",
                        minHeight: "350px",
                        height: "100%",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <HighchartsReact
                        ref={pieChartRef}
                        highcharts={Highcharts}
                        options={{
                          chart: {
                            type: "pie",
                            height: null, // Let chart size to container - will be calculated by Highcharts
                            backgroundColor: "transparent", // 👈 removes chart background
                            plotBackgroundColor: "transparent",
                            reflow: true, // Enable responsive reflow
                          },
                        title: {
                            text: "Case Status Distribution",
                          style: {
                              fontSize: "16px",
                              fontWeight: "bold",
                              color: colors?.text?.primary,
                            },
                        },
                        plotOptions: {
                          pie: {
                            allowPointSelect: true,
                              cursor: "pointer",
                            dataLabels: {
                              enabled: true,
                                format:
                                  "<b>{point.name}</b>: {point.percentage:.1f}%",
                                style: {
                                  color: colors?.text?.secondary,
                                  fontSize: "13px",
                                  fontFamily: "Roboto, sans-serif",
                                },
                            },
                            showInLegend: true,
                            point: {
                              events: {
                                  click: function () {
                                  const status = this.name;
                                  const isCurrentlySelected = caseFilters?.caseStatus &&
                                    Array.isArray(caseFilters.caseStatus) &&
                                    caseFilters.caseStatus.includes(status);

                                  // If clicking the same slice, clear the filter (toggle off)
                                  // Otherwise, apply the filter for the clicked slice
                                  const newFilters = isCurrentlySelected
                                    ? { ...caseFilters, caseStatus: [] }
                                    : { ...caseFilters, caseStatus: [status] };

                                  setCaseFilters(newFilters);

                                  // Update URL parameters
                                  const url = new URL(window.location.href);
                                    if (isCurrentlySelected) {
                                      url.searchParams.delete("caseStatus");
                                    } else {
                                      url.searchParams.delete("caseStatus");
                                      url.searchParams.append("caseStatus", status);
                                    }
                                  window.history.replaceState({}, "", url);

                                  // Update session storage
                                    sessionStorage.setItem(
                                      "caseFilters",
                                      JSON.stringify(newFilters)
                                    );

                                  // Dispatch custom event to notify that filters have changed
                                    window.dispatchEvent(
                                      new CustomEvent("caseFiltersChanged", {
                                        detail: { filters: newFilters },
                                      })
                                    );

                                  // Prevent default selection toggle - we handle it via filters
                                  return false;
                                  },
                                },
                              },
                            },
                          },
                          legend: {
                            itemStyle: {
                              color: colors?.text?.muted,
                              fontSize: "12px",
                              fontFamily: "Roboto, sans-serif",
                              fontWeight: "400",
                            },
                            itemHoverStyle: {
                              color: colors?.text?.secondary,
                            },
                            itemHiddenStyle: {
                              color: "#ccc",
                            },
                          },
                          series: [
                            {
                              name: "Cases",
                          colorByPoint: true,
                              data: finalCaseStatusData.caseStatusStats.map(
                                (item) => {
                                  // Check if this status is currently filtered (selected)
                                  const isSelected = caseFilters?.caseStatus &&
                                    Array.isArray(caseFilters.caseStatus) &&
                                    caseFilters.caseStatus.includes(item.status);

                                  return {
                                    name: item.status,
                                    y: item.count,
                                    color: getStatusColor(item.status, colors),
                                    selected: isSelected, // Keep slice visually selected if it's the active filter
                                    sliced: isSelected, // Pop out the slice if it's selected
                                  };
                                }
                              ),
                            },
                          ],
                        credits: {
                            enabled: false,
                          },
                        }}
                        containerProps={{
                          style: { height: "100%", width: "100%" },
                        }}
                      />
                    </Box>
                  ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {isCaseStatusLoading ? (
                          <>
                            <Skeleton
                              variant="circular"
                              width={200}
                              height={200}
                              animation="wave"
                              sx={{ backgroundColor: colors.background.secondary }}
                            />
                            <Skeleton
                              variant="rectangular"
                              width={300}
                              height={40}
                              animation="wave"
                              sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                            />
                          </>
                        ) : finalCaseStatusData?.status === "error" ? (
                          <BodyMedium sx={{ color: colors?.text?.muted }}>
                            {`Error: ${finalCaseStatusData.msg || "Failed to load case status data"}`}
                          </BodyMedium>
                        ) : (
                          <BodyMedium sx={{ color: colors?.text?.muted }}>
                            No case status data available
                          </BodyMedium>
                        )}
                    </Box>
                  )}
                </Box>
              </Grid>

                {/* Category Breakdown */}
              <Grid item xs={12}>
                  <Box
                    sx={{
                      minHeight: "200px",
                      maxHeight: "400px",
                  borderRadius: "1rem",
                      border: `1px solid ${colors?.border?.card}`,
                      backgroundColor: colors?.background?.card,
                  padding: "1.5rem",
                      overflow: "hidden",
                    }}
                  >
                    {issueSummaryData?.status === "ok" &&
                    issueSummaryData.issueSummaryStats &&
                    issueSummaryData.issueSummaryStats.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          display="flex"
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <LabelLarge>Category Breakdown</LabelLarge>
                        <Box
                          onClick={() => {
                              setActiveView("allCases");

                              // Check if there's a currently selected issue filter
                              const currentIssueFilter = caseFilters.issue;
                              let newFilters = {};

                              if (
                                currentIssueFilter &&
                                currentIssueFilter.length > 0
                              ) {
                                // Apply the currently selected issue filter
                                newFilters = {
                                  issue: currentIssueFilter,
                                };
                              } else {
                                // If no issue is selected, clear all filters
                                newFilters = {};

                                // Clear processed case status data to show all cases
                                setProcessedCaseStatusData({
                                  status: "ok",
                                  caseStatusStats: [],
                                  totalCases: 0
                                });
                              }

                            setCaseFilters(newFilters);
                              sessionStorage.setItem(
                                "caseFilters",
                                JSON.stringify(newFilters)
                              );

                              // Update URL parameters
                              const url = new URL(window.location.href);
                              url.search = ""; // Clear all search parameters

                              if (
                                newFilters.issue &&
                                newFilters.issue.length > 0
                              ) {
                                newFilters.issue.forEach((issue) => {
                                  url.searchParams.append("issue", issue);
                                });
                              }

                              window.history.replaceState({}, "", url);

                              window.dispatchEvent(
                                new CustomEvent("caseFiltersChanged", {
                                  detail: { filters: newFilters },
                                })
                              );
                          }}
                          sx={{
                            cursor: "pointer",
                            fontSize: "1.25rem",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                                transform: "scale(1.1)",
                              },
                          }}
                        >
                          <span
                            className="material-symbols-outlined"
                              style={{
                                fontSize: "1.1rem",
                                color: colors?.text?.muted,
                              }}
                          >
                            open_in_new
                          </span>
                        </Box>
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                          justifyContent="flex-start"
                      >
                        {(() => {
                          const total = issueSummaryData.totalCases;
                          const allStats = issueSummaryData.issueSummaryStats; // Data from backend

                          // Define custom order for issues
                          const customIssueOrder = [
                            "Hash Board",
                            "Power Supply",
                            "Fans Chassis",
                            "PSU Fans",
                            "Performance",
                            "Network/Connectivity",
                            "Mining Pools/ZTP",
                              "Configuration",
                          ];

                          // Sort by custom order first, then by count for remaining items
                          const sortedStats = allStats.sort((a, b) => {
                            const aIndex = customIssueOrder.indexOf(a.issue);
                            const bIndex = customIssueOrder.indexOf(b.issue);

                            // If both are in custom order, sort by their position
                            if (aIndex !== -1 && bIndex !== -1) {
                              return aIndex - bIndex;
                            }
                            // If only a is in custom order, a comes first
                            if (aIndex !== -1 && bIndex === -1) {
                              return -1;
                            }
                            // If only b is in custom order, b comes first
                            if (aIndex === -1 && bIndex !== -1) {
                              return 1;
                            }
                            // If neither is in custom order, sort by count descending
                            return b.count - a.count;
                          });

                            return Array.from(
                              { length: Math.ceil(sortedStats.length / 3) },
                              (_, rowIndex) => (
                                <Box
                                  key={rowIndex}
                                  sx={{
                                    display: "flex",
                                    gap: "1rem",
                                    paddingBottom: "1rem",
                                  }}
                                >
                              {[0, 1, 2].map((colIndex) => {
                                const itemIndex = rowIndex * 3 + colIndex;
                                const item = sortedStats[itemIndex];

                                    if (!item)
                                      return (
                                        <Box key={colIndex} sx={{ flex: 1 }} />
                                      );

                                    const percentage =
                                      total > 0
                                        ? (item.count / total) * 100
                                        : 0;

                                return (
                                  <Box key={colIndex} sx={{ flex: 1 }}>
                                    <LinearProgressWithLabel
                                      value={percentage}
                                      label={item.issue}
                                      count={item.count}
                                      total={total}
                                          color={getIssueColor(
                                            item.issue,
                                            colors
                                          )}
                                          colors={colors}
                                          onClick={async () => {
                                            console.log('Clicked on issue:', item.issue);

                                        // Clear all existing filters and set only the clicked issue
                                        const newFilters = {
                                              issue: [item.issue],
                                        };

                                        // Update case filters
                                        setCaseFilters(newFilters);

                                            // Call PostCaseList API with the issue filter
                                            try {
                                              console.log('Calling PostCaseList API with filters:', newFilters);
                                              const response = await PostCaseList({
                                                authToken,
                                                body: {
                                                  filters: newFilters,
                                                  sort: {},
                                                  pagination: { limit: 25, skip: 0 }
                                                }
                                              });

                                              console.log('PostCaseList API response:', response);

                                              // Process the response for pie chart
                                              const processedData = processCaseStatusForPieChart(response);
                                              console.log('Processed data for pie chart:', processedData);

                                              // Update the processed case status data
                                              setProcessedCaseStatusData(processedData);

                                            } catch (error) {
                                              console.error('Error calling PostCaseList API:', error);
                                            }

                                        // Clear all URL parameters and set only issue
                                            const url = new URL(
                                              window.location.href
                                            );
                                            url.search = ""; // Clear all search parameters
                                            url.searchParams.append(
                                              "issue",
                                              item.issue
                                            ); // Add only the issue param

                                        // Update browser URL
                                            window.history.replaceState(
                                              {},
                                              "",
                                              url
                                            );

                                        // Update session storage
                                            sessionStorage.setItem(
                                              "caseFilters",
                                              JSON.stringify(newFilters)
                                            );

                                        // Dispatch custom event to notify that filters have changed
                                            window.dispatchEvent(
                                              new CustomEvent(
                                                "caseFiltersChanged",
                                                {
                                                  detail: {
                                                    filters: newFilters,
                                                  },
                                                }
                                              )
                                            );
                                      }}
                                    />
                                  </Box>
                                );
                              })}
                            </Box>
                              )
                            );
                        })()}
                      </Box>
                    </Box>
                  ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {isIssueSummaryLoading ? (
                          <>
                            <Skeleton
                              variant="rectangular"
                              width="100%"
                              height={150}
                              animation="wave"
                              sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                            />
                            <Skeleton
                              variant="rectangular"
                              width="80%"
                              height={20}
                              animation="wave"
                              sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                            />
                          </>
                        ) : (
                          <BodyMedium sx={{ color: colors?.text?.muted }}>
                            No issue summary data available
                      </BodyMedium>
                        )}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
            </Box>
          </Box>
        ) : (
          // All Cases View
          <>
            {/* All Cases Section */}
            <Grid
              container
              spacing={0}
              sx={{
                height: "calc(100vh - 12rem)",
                transition: "width 0.3s ease-in-out",
              }}
            >
          <Grid
            item
            xs={rightSidePanelOpen ? 9 : 12}
            sx={{
              transition: "width 0.3s ease-in-out",
              width: "100%",
            }}
          >
            <Box
              className={"miner-page-table"}
              display={"flex"}
              flexDirection={"row"}
              backgroundColor={colors?.background?.paper}
              padding={"0.75rem 0  0.75rem 0"}
              borderRadius={"1rem"}
              sx={{
                transition: "width 0.3s ease-in-out",
                width: "100%",
              }}
            >
              <Box
                className={"table-container"}
                display={"flex"}
                flexDirection={"column"}
                padding={"0 0.75rem 0 0.75rem"}
                height={"auto"}
                minWidth={"0"}
                gap={4}
                flex={3}
                flexShrink={1}
                sx={{
                  width: "100%",
                }}
              >
              <CaseManagementHeader
                gridRef={tableContainerRef}
                totalCount={totalCount || 0}
                selectedCases={selectedCases}
                setRightSidePanelOpen={handleRightSidePanelToggle}
                rightSidePanelOpen={rightSidePanelOpen}
              />

              <CaseManagementOperationsContainer
                  gridRef={tableContainerRef}
                  refetch={minerPageRefetch}
                  resetTableSelection={resetTableSelection}
                  setTableKey={setTableKey}
                  user={user}
                  selectedCases={selectedCases}
                  caseData={tableData || []}
                />
                <Box
                  className={"Datagrid-container"}
                  display={"flex"}
                  flexShrink={1}
                  flexGrow={1}
                  sx={{
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                      <SimpleErrorBoundary>
                        <DataGridWrapper
                          key={tableKey}
                          colors={colors}
                          config={dataGridProps}
                          height="calc(100vh - 23rem)"
                        />
                      </SimpleErrorBoundary>
                </Box>
              </Box>
            </Box>
          </Grid>
          {rightSidePanelOpen && (
            <Grid
              item
              xs={3}
              sx={{
                transition: "all 0.3s ease-in-out",
                    transform: rightSidePanelOpen
                      ? "translateX(0)"
                      : "translateX(100%)",
                opacity: rightSidePanelOpen ? 1 : 0,
                visibility: rightSidePanelOpen ? "visible" : "hidden",
              }}
            >
                <Box
                  sx={{
                      backgroundColor: colors?.background?.paper,
                    borderRadius: "1rem",
                    padding: "1rem",
                    height: "calc(100vh - 12rem)",
                    marginLeft: "1rem",
                    overflowY: "auto",
                  }}
                >
                  <H7TitleLarge>Case Details</H7TitleLarge>
                  {selectedCases && selectedCases.length === 1 && selectedCaseId && (
                    <Box sx={{ mt: 2 }}>
                      <BodyMedium sx={{ fontWeight: "bold", mb: 2 }}>
                        Case History:
                      </BodyMedium>
                      {commentHistoryLoading ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              py: 2,
                            }}
                          >
                            <StyledLinearProgress colors={colors} />
                        </Box>
                        ) : commentHistoryData?.msg &&
                          commentHistoryData.msg.length > 0 &&
                          selectedCaseId ? (
                          <>
                            {(() => {
                              // Handle different response structures
                              let commentsToRender = [];

                              if (commentHistoryData.msg.length > 0) {
                                const firstItem = commentHistoryData.msg[0];

                                // Check if it's the nested structure: msg[0].comments
                                if (firstItem && firstItem.comments && Array.isArray(firstItem.comments)) {
                                  commentsToRender = firstItem.comments;
                                }
                                // Check if it's direct comment objects (even with empty updatedBy)
                                else if (firstItem && firstItem.updatedAt && firstItem.comment) {
                                  commentsToRender = commentHistoryData.msg;
                                }
                                // Check if it's string array
                                else if (typeof firstItem === 'string') {
                                  commentsToRender = commentHistoryData.msg.map(comment => ({
                                    comment: comment,
                                    updatedBy: 'Unknown',
                                    updatedAt: new Date().toISOString()
                                  }));
                                } else {
                                  // Ensure we have valid comment objects even for unknown structures
                                  commentsToRender = commentHistoryData.msg.map(item => ({
                                    comment: item.comment || item,
                                    updatedBy: item.updatedBy || '',
                                    updatedAt: item.updatedAt || new Date().toISOString()
                                  }));
                                }
                              }

                              return commentsToRender.map((commentItem, index) => {
                                return (
                            <Box
                              key={index}
                              sx={{
                                      backgroundColor: colors?.background?.card,
                                padding: "1rem",
                                borderRadius: "0.5rem",
                                      border: `1px solid ${colors?.border?.default}`,
                                mb: 2,
                              }}
                            >
                              <BodyMedium sx={{ mb: 1 }}>
                                {commentItem.comment}
                              </BodyMedium>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <BodySmall
                                        sx={{
                                          color: colors?.text?.muted,
                                          fontWeight: "500",
                                        }}
                                      >
                                  Updated by: {commentItem.updatedBy}
                                </BodySmall>
                                      <BodySmall
                                        sx={{ color: colors?.text?.muted }}
                                      >
                                        {formatInTimeZone(
                                          parseISO(commentItem.updatedAt),
                                          Intl.DateTimeFormat().resolvedOptions()
                                            .timeZone,
                                          "MMM dd, yyyy hh:mm a"
                                        )}
                                </BodySmall>
                              </Box>
                            </Box>
                                );
                              });
                            })()}
                        </>
                      ) : (
                          <BodyMedium
                            sx={{
                              backgroundColor: colors?.background?.accent,
                          padding: "1rem",
                          borderRadius: "0.5rem",
                              border: `1px solid ${colors?.border?.default}`,
                            }}
                          >
                            No comments yet
                        </BodyMedium>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
          )}
            </Grid>
          </>
        )}
      </PageWrapper>
    </RefetchContext.Provider>
  );
}
